import React, { useEffect, useRef, useState } from 'react';
import { getToken } from '../utils/auth';

// A voice message on the web (2026-11-01). The clip comes from the authenticated endpoint
// (GET /api/messages/:id/audio — never a public URL), so it is fetched with the token on the first
// play and handed to the browser's own <audio> as a blob. AAC in MP4, which every browser plays.
const SERVER = process.env.REACT_APP_SERVER_URL || 'http://localhost:3001';

const clock = (ms) => {
  const s = Math.max(0, Math.round((ms || 0) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export default function VoiceMessage({ id, durationMs }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);

  // The blob URL is ours to release when this message leaves the screen.
  useEffect(() => () => { if (src) URL.revokeObjectURL(src); }, [src]);

  const load = async () => {
    if (src || loading || !id) return;
    setLoading(true);
    try {
      const res = await fetch(`${SERVER}/api/messages/${id}/audio`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error(String(res.status));
      setSrc(URL.createObjectURL(await res.blob()));
    } catch (_) {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  // Played as soon as it has arrived, from the same click that asked for it.
  useEffect(() => { if (src) audioRef.current?.play().catch(() => {}); }, [src]);

  if (failed) return <span className="voice-msg voice-msg--failed">Voice message ({clock(durationMs)}) — could not load</span>;
  if (!src) {
    return (
      <button type="button" className="voice-msg voice-msg--load" onClick={load} disabled={loading} aria-label="Play voice message">
        <span aria-hidden="true">{loading ? '…' : '▶'}</span> Voice message · {clock(durationMs)}
      </button>
    );
  }
  return <audio ref={audioRef} className="voice-msg__audio" src={src} controls preload="auto" />;
}
